// Measurement grading: type the base (sample) size once, give each point of measure a grade (how much it grows per
// size) and Falcon works out every other size. Size = base + the jumps between it and the base; a jump is the row's
// grade unless that size has its own ("uneven steps"). Sizes typed over by hand keep what was typed. The worked-out
// values are stored in the row's values, so QC, the PDF and everything else read them as before.
import { parseMeasure, parseTolerance } from "./qcwizard.js";

export interface GradedRow { values: Record<string, string>; base?: string; grade?: string; steps?: Record<string, string>; manual?: string[] }

/** A number in the unit: cm and mm to two decimals, inches to the nearest eighth written as 22 1/4. */
export function formatMeasure(n: number, unit: string): string {
  if (!Number.isFinite(n)) return "";
  if (unit !== "inch") return String(Math.round(n * 100) / 100);
  const neg = n < 0, eighths = Math.round(Math.abs(n) * 8);
  const whole = Math.floor(eighths / 8), rest = eighths % 8;
  const g = rest ? (rest % 4 === 0 ? 2 : rest % 2 === 0 ? 4 : 8) : 1;
  const frac = rest ? `${rest / (8 / g)}/${g}` : "";
  return (neg ? "-" : "") + (whole ? (frac ? `${whole} ${frac}` : String(whole)) : frac || "0");
}

/** Is the row graded (it has a base size and a grade or its own steps)? */
export function isGraded(r: GradedRow, sizes: string[]): boolean {
  return !!r.base && sizes.includes(r.base) && (parseMeasure(r.grade || "") !== null || Object.values(r.steps || {}).some((v) => parseMeasure(v) !== null));
}

/** The jump into a size from its neighbour on the base's side (always positive going away from the base). */
export function stepInto(r: GradedRow, size: string): number {
  const own = parseMeasure(r.steps?.[size] ?? "");
  return own ?? parseMeasure(r.grade || "") ?? 0;
}

/** Every size's value by the grade rule (ignoring sizes typed over by hand). */
export function gradeByRule(r: GradedRow, sizes: string[], unit: string): Record<string, string> {
  const out: Record<string, string> = {};
  const b = sizes.indexOf(r.base || "");
  const base = parseMeasure(r.values[r.base || ""] ?? "");
  if (b < 0 || base === null) return out;
  out[sizes[b]] = r.values[sizes[b]];
  let v = base;
  for (let i = b + 1; i < sizes.length; i++) { v += stepInto(r, sizes[i]); out[sizes[i]] = formatMeasure(v, unit); }
  v = base;
  for (let i = b - 1; i >= 0; i--) { v -= stepInto(r, sizes[i]); out[sizes[i]] = formatMeasure(v, unit); }
  return out;
}

/** The row's values with every graded size worked out; sizes typed over by hand keep their value. */
export function gradeRow<T extends GradedRow>(r: T, sizes: string[], unit: string): T {
  if (!isGraded(r, sizes)) return r;
  const rule = gradeByRule(r, sizes, unit);
  const manual = new Set(r.manual || []);
  const values = Object.fromEntries(sizes.map((s) => [s, manual.has(s) && s !== r.base ? r.values[s] ?? "" : rule[s] ?? r.values[s] ?? ""]));
  return { ...r, values };
}

/** The grade as printed: "2.5", or "2.5 (2XL +3, 3XL +3)" when some sizes have their own step. */
export function gradeText(r: GradedRow, sizes: string[]): string {
  const own = sizes.filter((s) => s !== r.base && (r.steps?.[s] ?? "") !== "").map((s) => `${s} ${r.steps![s]}`);
  return [r.grade || "", own.length ? `(${own.join(", ")})` : ""].filter(Boolean).join(" ");
}

/** One measurement converted between units (cm, mm, inch); text that isn't a number is kept. */
export function convertMeasure(v: string, from: string, to: string): string {
  const n = parseMeasure(v);
  if (n === null || from === to) return v;
  const mm = { mm: 1, cm: 10, inch: 25.4 } as Record<string, number>;
  return formatMeasure((n * (mm[from] || 10)) / (mm[to] || 10), to);
}
/** A tolerance converted between units: ±1 cm becomes ±3/8 inch, +1/-0.5 becomes +3/8/-1/4. */
export function convertTolerance(v: string, from: string, to: string): string {
  const t = parseTolerance(v);
  if (!t || from === to) return v;
  const c = (n: number) => convertMeasure(String(n), from, to);
  return t.plus === t.minus ? `±${c(t.plus)}` : `+${c(t.plus)} -${c(t.minus)}`;
}
/** A row converted between units. Graded rows convert the base and the grade and are worked out again. */
export function convertRow<T extends GradedRow & { tolerance: string }>(r: T, sizes: string[], from: string, to: string): T {
  const values = Object.fromEntries(Object.entries(r.values).map(([s, v]) => [s, convertMeasure(v, from, to)]));
  const steps = r.steps ? Object.fromEntries(Object.entries(r.steps).map(([s, v]) => [s, convertMeasure(v, from, to)])) : undefined;
  const out = { ...r, values, tolerance: convertTolerance(r.tolerance, from, to), ...(r.grade !== undefined ? { grade: convertMeasure(r.grade, from, to) } : {}), ...(steps ? { steps } : {}) };
  return gradeRow(out, sizes, to);
}
